import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    Button,
    Avatar,
    IconButton,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import { useTheme } from '@mui/material/styles';
import ScheduleIcon from '@mui/icons-material/Schedule';

import BoilerSwitch from './BoilerSwitch';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import DeviceCardChart from './DeviceCardChart';
import DeviceCardEditModal from './DeviceCardEditModal';
import ScheduleModal from './ScheduleModal';
import ScheduleDeviceModal from './ScheduleDeviceModal';
import { deleteDevice, toggleBoilerSwitch } from '../../api_calls/devices';

const DeviceCard = ({ device, refreshDevices, setSnackbar }) => {
    const theme = useTheme();
    const isBoiler = device.name.toLowerCase().includes('boiler');
    const [isOn, setIsOn] = useState(isBoiler ? (device.output || false) : false);
    const [editOpen, setEditOpen] = useState(false);
    const [scheduleOpen, setScheduleOpen] = useState(false);
    const [scheduleDeviceOpen, setScheduleDeviceOpen] = useState(false);
    const [selectedSchedule, setSelectedSchedule] = useState(null);
    const [message, setMessage] = useState(null);

    const handleToggle = () => {
        setMessage(null);
        if (isBoiler) {
            toggleBoilerSwitch(device.device_id)
                .then((response) => {
                    const newIsOn = !response.data.result.was_on;
                    setIsOn(newIsOn);
                    setMessage({
                        type: 'success',
                        text: `${device.name} turned ${newIsOn ? 'ON' : 'OFF'}`,
                    });
                })
                .catch(() => {
                    setMessage({
                        type: 'error',
                        text: `Failed to toggle ${device.name}.`,
                    });
                });
        } else {
            setIsOn(prev => !prev);
        }
    };
    const handleEditOpen = () => setEditOpen(true);
    const handleEditClose = () => setEditOpen(false);

    const handleScheduleOpen = () => setScheduleOpen(true);
    const handleScheduleClose = () => setScheduleOpen(false);

    const handleScheduleDeviceOpen = (schedule = null) => {
        setSelectedSchedule(schedule);
        setScheduleDeviceOpen(true);
        setScheduleOpen(false);
    };
    const handleScheduleDeviceClose = () => {
        setScheduleDeviceOpen(false);
        setSelectedSchedule(null);
    };

    const handleScheduleSuccess = () => {
        // Close schedule device modal and reopen schedule modal to refresh
        setScheduleDeviceOpen(false);
        setSelectedSchedule(null);
        // Small delay to ensure modal state is updated
        setTimeout(() => {
            setScheduleOpen(true);
        }, 100);
    };

    const handleDelete = () => {
        deleteDevice(device.id)
            .then(() => {
                setSnackbar({
                    open: true,
                    message: 'Device deleted successfully!',
                    severity: 'success',
                });
                handleEditClose();
                refreshDevices();
            })
            .catch(() => {
                setSnackbar({
                    open: true,
                    message: 'Failed to delete device.',
                    severity: 'error',
                });
            });
    };

    useEffect(() => {
        if (message) {
            const timer = setTimeout(() => setMessage(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    return (
        <>
            <Card
                sx={{
                    borderRadius: '25px',
                    padding: 2,
                    height: 350,
                    display: 'flex',
                    flexDirection: 'column',
                    boxShadow: 'none',
                }}
            >
                <CardContent
                    sx={{
                        padding: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                    }}
                >
                    <Box sx={{ flex: '1 1 auto' }}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 1,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar sx={{ width: 36, height: 36, bgcolor: theme.palette.secondary.main }}>
                                    <DevicesOtherIcon sx={{ color: theme.palette.common.white, fontSize: 20 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        {device.name}
                                    </Typography>
                                    <Typography variant="caption" color="success.main">
                                        ● Online
                                    </Typography>
                                </Box>
                            </Box>

                            {isBoiler && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: !isOn ? 'bold' : 'normal',
                                            color: !isOn ? 'error.main' : 'text.primary',
                                        }}
                                    >
                                        Off
                                    </Typography>
                                    <BoilerSwitch checked={isOn} onChange={handleToggle} />
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: isOn ? 'bold' : 'normal',
                                            color: isOn ? 'green' : 'text.primary',
                                        }}
                                    >
                                        On
                                    </Typography>
                                </Box>
                            )}

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {isBoiler && (
                                    <IconButton
                                        size="small"
                                        onClick={handleScheduleOpen}
                                        sx={{
                                            color: 'primary.main',
                                            '&:hover': {
                                                bgcolor: 'primary.light',
                                                color: 'white',
                                            },
                                        }}
                                    >
                                        <ScheduleIcon />
                                    </IconButton>
                                )}
                                {!device.isDemoDevice && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        sx={{ borderRadius: '12px', textTransform: 'none' }}
                                        onClick={handleEditOpen}
                                    >
                                        Configure
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    {/* Place the Alert below the ON/OFF switch and Configure button, above the chart */}
                    {isBoiler && message && (
                        <MuiAlert severity={message.type} sx={{ my: 1 }}>
                            {message.text}
                        </MuiAlert>
                    )}

                    <Box sx={{ mt: 2 }}>
                        <DeviceCardChart device={device} />
                    </Box>
                </CardContent>
            </Card>

            <DeviceCardEditModal
                open={editOpen}
                onClose={handleEditClose}
                device={device}
                onDelete={handleDelete}
                refreshDevices={refreshDevices}
            />

            {isBoiler && (
                <>
                    <ScheduleModal
                        open={scheduleOpen}
                        onClose={handleScheduleClose}
                        device={device}
                        onEditSchedule={handleScheduleDeviceOpen}
                        onCreateSchedule={() => handleScheduleDeviceOpen(null)}
                    />
                    <ScheduleDeviceModal
                        open={scheduleDeviceOpen}
                        onClose={handleScheduleDeviceClose}
                        device={device}
                        schedule={selectedSchedule}
                        onSuccess={handleScheduleSuccess}
                    />
                </>
            )}
        </>
    );
};

export default DeviceCard;
